import { newUlid } from '../entity/catalog';
import type { Arc } from '../plan/arc';
import type { FileEdit } from '../plan/edits';

/**
 * A storyline minted from selected arcs.
 *
 * A storyline IS an arc — same entity, same status set — whose members ride
 * an `arcs:` list. So creating one means writing one more arc file, not a new
 * kind of thing.
 *
 * The web can do this, contrary to what the design assumed. A manifest-less
 * file declaring `kind: arc` is minted as a real arc by the importer
 * (`VaultImporter.mintArcFromFile`), which takes its name from the `# Title`,
 * its body from the prose, and flows `arcs:` back as membership. The file
 * then gets its id and manifest on re-export, and every peer adopts it as a
 * normal owned arc.
 *
 * It is written with the id it will have: the importer takes a declared id as
 * the unit's own, so the board can name the storyline before any device has
 * adopted it. Nothing manifest-shaped — that is the app's to write.
 */
export function newStorylineEdit(name: string, members: Arc[], id: string = newUlid()): FileEdit | null {
  const title = name.trim();
  if (!title || members.length < 2) return null;

  const path = `arcs/${safeFilename(title)}.md`;
  const memberLines = members.map((m) => `  - ${m.path}\n`).join('');

  // A storyline's status is its OWN — nothing is derived from its members and
  // nothing cascades into them. `active` is a starting point the user can
  // change, not a reading of what it holds.
  const text =
    `---\nid: ${id}\nkind: arc\nstatus: active\norder: 0\narcs:\n${memberLines}---\n\n` +
    `# ${title}\n\n` +
    `Grouped from the board.\n`;

  return { path, text };
}

/** A vault path is a filename, so the characters a filesystem refuses have to
 *  go. Everything else — spaces, em dashes, accents — stays, because arc
 *  filenames are read by people. */
export function safeFilename(title: string): string {
  return title
    .replace(/[\\/:*?"<>|]/g, '-')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 120);
}
